import { useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../store';
import { setPosts, setPagination, setLoading, setError, setCurrentPost } from '../store/slices/postSlice';
import { postsApi } from '../api/posts';
import type { Post, PostFilters, PaginatedResponse } from '../types/post';
import { useModerator } from './useModerator';

export const usePosts = (filters?: PostFilters) => {
  const dispatch = useDispatch<AppDispatch>();
  const { items, currentPost, pagination, loading, error } = useSelector(
    (state: RootState) => state.posts
  );
  const isModerator = useModerator();

  const [localFilters, setLocalFilters] = useState<PostFilters | undefined>(filters);

  // Use a ref for pagination to avoid re-creating fetchPosts when pagination updates.
  // This breaks the cycle: fetchPosts updates pagination → new fetchPosts ref → useEffect re-runs → fetchPosts again.
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  const fetchPosts = useCallback(async (newFilters?: PostFilters & { includeDeleted?: boolean; deletedOnly?: boolean }) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      
      const activeFilters = newFilters || localFilters || {};
      const response: PaginatedResponse<Post> = await postsApi.getAllPosts({
        ...activeFilters,
        page: activeFilters.page ?? paginationRef.current.page,
        size: activeFilters.size ?? paginationRef.current.size,
      });

      dispatch(setPosts(response.content));
      dispatch(setPagination({
        page: response.number,
        size: response.size,
        totalElements: response.totalElements,
        totalPages: response.totalPages,
        first: response.first,
        last: response.last,
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch posts';
      dispatch(setError(errorMessage));
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, localFilters]);

  const fetchPost = useCallback(async (id: string, includeDeleted?: boolean) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      // If includeDeleted is not explicitly set, try to fetch normally first
      // If it fails with 404 and user is moderator, try again with includeDeleted=true
      try {
        const post = await postsApi.getPost(id, includeDeleted ?? false);
        dispatch(setCurrentPost(post));
        return post;
      } catch (err: any) {
        // If 404 and user is moderator, try with includeDeleted=true
        if (includeDeleted === undefined && isModerator && err?.response?.status === 404) {
          const post = await postsApi.getPost(id, true);
          dispatch(setCurrentPost(post));
          return post;
        }
        throw err;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch post';
      dispatch(setError(errorMessage));
      throw err;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, isModerator]);

  return {
    posts: items,
    currentPost,
    pagination,
    loading,
    error,
    fetchPosts,
    fetchPost,
    setFilters: setLocalFilters,
    refetch: () => fetchPosts(localFilters),
  };
};

